import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, PlayCircle, PauseCircle, Volume2, Search, Loader, FileText, Info } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ScrollArea } from './ui/scroll-area';

const QuranReaderWithSync = () => {
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [verseData, setVerseData] = useState(null);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [showTranslation, setShowTranslation] = useState(true);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [showFootnotes, setShowFootnotes] = useState({});
  const audioRef = useRef(null);
  const verseRefs = useRef({});

  // Lista e 114 surrave
  const allSurahs = [
    { id: 1, name: 'El-Fatiha', arabic: 'الفاتحة', verses: 7 },
    { id: 2, name: 'El-Bekare', arabic: 'البقرة', verses: 286 },
    { id: 3, name: 'Ali Imran', arabic: 'آل عمران', verses: 200 },
    { id: 4, name: 'En-Nisa', arabic: 'النساء', verses: 176 },
    { id: 5, name: 'El-Maide', arabic: 'المائدة', verses: 120 },
    { id: 6, name: 'El-Enam', arabic: 'الأنعام', verses: 165 },
    { id: 7, name: 'El-Araf', arabic: 'الأعراف', verses: 206 },
    { id: 8, name: 'El-Enfal', arabic: 'الأنفال', verses: 75 },
    { id: 9, name: 'Et-Teube', arabic: 'التوبة', verses: 129 },
    { id: 10, name: 'Junus', arabic: 'يونس', verses: 109 },
    { id: 11, name: 'Hud', arabic: 'هود', verses: 123 },
    { id: 12, name: 'Jusuf', arabic: 'يوسف', verses: 111 },
    { id: 13, name: 'Er-Rad', arabic: 'الرعد', verses: 43 },
    { id: 14, name: 'Ibrahim', arabic: 'إبراهيم', verses: 52 },
    { id: 15, name: 'El-Hixhr', arabic: 'الحجر', verses: 99 },
    { id: 16, name: 'En-Nahl', arabic: 'النحل', verses: 128 },
    { id: 17, name: 'El-Isra', arabic: 'الإسراء', verses: 111 },
    { id: 18, name: 'El-Kehf', arabic: 'الكهف', verses: 110 },
    { id: 19, name: 'Merjem', arabic: 'مريم', verses: 98 },
    { id: 20, name: 'Ta-Ha', arabic: 'طه', verses: 135 },
    { id: 21, name: 'El-Enbija', arabic: 'الأنبياء', verses: 112 },
    { id: 22, name: 'El-Haxh', arabic: 'الحج', verses: 78 },
    { id: 23, name: 'El-Muminun', arabic: 'المؤمنون', verses: 118 },
    { id: 24, name: 'En-Nur', arabic: 'النور', verses: 64 },
    { id: 25, name: 'El-Furkan', arabic: 'الفرقان', verses: 77 },
    { id: 26, name: 'Esh-Shuara', arabic: 'الشعراء', verses: 227 },
    { id: 27, name: 'En-Neml', arabic: 'النمل', verses: 93 },
    { id: 28, name: 'El-Kasas', arabic: 'القصص', verses: 88 },
    { id: 29, name: 'El-Ankebut', arabic: 'العنكبوت', verses: 69 },
    { id: 30, name: 'Er-Rum', arabic: 'الروم', verses: 60 },
    { id: 31, name: 'Lukman', arabic: 'لقمان', verses: 34 },
    { id: 32, name: 'Es-Sexhde', arabic: 'السجدة', verses: 30 },
    { id: 33, name: 'El-Ahzab', arabic: 'الأحزاب', verses: 73 },
    { id: 34, name: 'Sebe', arabic: 'سبأ', verses: 54 },
    { id: 35, name: 'Fatir', arabic: 'فاطر', verses: 45 },
    { id: 36, name: 'Jasin', arabic: 'يس', verses: 83 },
    { id: 37, name: 'Es-Saffat', arabic: 'الصافات', verses: 182 },
    { id: 38, name: 'Sad', arabic: 'ص', verses: 88 },
    { id: 39, name: 'Ez-Zumer', arabic: 'الزمر', verses: 75 },
    { id: 40, name: 'Gafir', arabic: 'غافر', verses: 85 },
    { id: 41, name: 'Fussilet', arabic: 'فصلت', verses: 54 },
    { id: 42, name: 'Esh-Shura', arabic: 'الشورى', verses: 53 },
    { id: 43, name: 'Ez-Zuhruf', arabic: 'الزخرف', verses: 89 },
    { id: 44, name: 'Ed-Duhan', arabic: 'الدخان', verses: 59 },
    { id: 45, name: 'El-Xhathije', arabic: 'الجاثية', verses: 37 },
    { id: 46, name: 'El-Ahkaf', arabic: 'الأحقاف', verses: 35 },
    { id: 47, name: 'Muhammed', arabic: 'محمد', verses: 38 },
    { id: 48, name: 'El-Fet\'h', arabic: 'الفتح', verses: 29 },
    { id: 49, name: 'El-Huxhurat', arabic: 'الحجرات', verses: 18 },
    { id: 50, name: 'Kaf', arabic: 'ق', verses: 45 },
    { id: 51, name: 'Edh-Dharijat', arabic: 'الذاريات', verses: 60 },
    { id: 52, name: 'Et-Tur', arabic: 'الطور', verses: 49 },
    { id: 53, name: 'En-Nexhm', arabic: 'النجم', verses: 62 },
    { id: 54, name: 'El-Kamer', arabic: 'القمر', verses: 55 },
    { id: 55, name: 'Er-Rahman', arabic: 'الرحمن', verses: 78 },
    { id: 56, name: 'El-Vakiah', arabic: 'الواقعة', verses: 96 },
    { id: 57, name: 'El-Hadid', arabic: 'الحديد', verses: 29 },
    { id: 58, name: 'El-Muxhadele', arabic: 'المجادلة', verses: 22 },
    { id: 59, name: 'El-Hashr', arabic: 'الحشر', verses: 24 },
    { id: 60, name: 'El-Mumtehine', arabic: 'الممتحنة', verses: 13 },
    { id: 61, name: 'Es-Saff', arabic: 'الصف', verses: 14 },
    { id: 62, name: 'El-Xhumua', arabic: 'الجمعة', verses: 11 },
    { id: 63, name: 'El-Munafikun', arabic: 'المنافقون', verses: 11 },
    { id: 64, name: 'Et-Tegabun', arabic: 'التغابن', verses: 18 },
    { id: 65, name: 'Et-Talak', arabic: 'الطلاق', verses: 12 },
    { id: 66, name: 'Et-Tahrim', arabic: 'التحريم', verses: 12 },
    { id: 67, name: 'El-Mulk', arabic: 'الملك', verses: 30 },
    { id: 68, name: 'El-Kalem', arabic: 'القلم', verses: 52 },
    { id: 69, name: 'El-Hakka', arabic: 'الحاقة', verses: 52 },
    { id: 70, name: 'El-Mearixh', arabic: 'المعارج', verses: 44 },
    { id: 71, name: 'Nuh', arabic: 'نوح', verses: 28 },
    { id: 72, name: 'El-Xhinn', arabic: 'الجن', verses: 28 },
    { id: 73, name: 'El-Muzemmil', arabic: 'المزمل', verses: 20 },
    { id: 74, name: 'El-Mudeththir', arabic: 'المدثر', verses: 56 },
    { id: 75, name: 'El-Kijame', arabic: 'القيامة', verses: 40 },
    { id: 76, name: 'El-Insan', arabic: 'الإنسان', verses: 31 },
    { id: 77, name: 'El-Murselat', arabic: 'المرسلات', verses: 50 },
    { id: 78, name: 'En-Nebe', arabic: 'النبأ', verses: 40 },
    { id: 79, name: 'En-Naziat', arabic: 'النازعات', verses: 46 },
    { id: 80, name: 'Abese', arabic: 'عبس', verses: 42 },
    { id: 81, name: 'Et-Tekvir', arabic: 'التكوير', verses: 29 },
    { id: 82, name: 'El-Infitar', arabic: 'الإنفطار', verses: 19 },
    { id: 83, name: 'El-Mutaffifin', arabic: 'المطففين', verses: 36 },
    { id: 84, name: 'El-Inshikak', arabic: 'الإنشقاق', verses: 25 },
    { id: 85, name: 'El-Buruxh', arabic: 'البروج', verses: 22 },
    { id: 86, name: 'Et-Tarik', arabic: 'الطارق', verses: 17 },
    { id: 87, name: 'El-Ala', arabic: 'الأعلى', verses: 19 },
    { id: 88, name: 'El-Gashije', arabic: 'الغاشية', verses: 26 },
    { id: 89, name: 'El-Fexhr', arabic: 'الفجر', verses: 30 },
    { id: 90, name: 'El-Beled', arabic: 'البلد', verses: 20 },
    { id: 91, name: 'Esh-Shems', arabic: 'الشمس', verses: 15 },
    { id: 92, name: 'El-Lejl', arabic: 'الليل', verses: 21 },
    { id: 93, name: 'Ed-Duha', arabic: 'الضحى', verses: 11 },
    { id: 94, name: 'Esh-Sherh', arabic: 'الشرح', verses: 8 },
    { id: 95, name: 'Et-Tin', arabic: 'التين', verses: 8 },
    { id: 96, name: 'El-Alak', arabic: 'العلق', verses: 19 },
    { id: 97, name: 'El-Kadr', arabic: 'القدر', verses: 5 },
    { id: 98, name: 'El-Bejjine', arabic: 'البينة', verses: 8 },
    { id: 99, name: 'Ez-Zelzele', arabic: 'الزلزلة', verses: 8 },
    { id: 100, name: 'El-Adijat', arabic: 'العاديات', verses: 11 },
    { id: 101, name: 'El-Karia', arabic: 'القارعة', verses: 11 },
    { id: 102, name: 'Et-Tekathur', arabic: 'التكاثر', verses: 8 },
    { id: 103, name: 'El-Asr', arabic: 'العصر', verses: 3 },
    { id: 104, name: 'El-Humeze', arabic: 'الهمزة', verses: 9 },
    { id: 105, name: 'El-Fil', arabic: 'الفيل', verses: 5 },
    { id: 106, name: 'Kurejsh', arabic: 'قريش', verses: 4 },
    { id: 107, name: 'El-Maun', arabic: 'الماعون', verses: 7 },
    { id: 108, name: 'El-Keuther', arabic: 'الكوثر', verses: 3 },
    { id: 109, name: 'El-Kafirun', arabic: 'الكافرون', verses: 6 },
    { id: 110, name: 'En-Nasr', arabic: 'النصر', verses: 3 },
    { id: 111, name: 'El-Mesed', arabic: 'المسد', verses: 5 },
    { id: 112, name: 'El-Ihlas', arabic: 'الإخلاص', verses: 4 },
    { id: 113, name: 'El-Felek', arabic: 'الفلق', verses: 5 },
    { id: 114, name: 'En-Nas', arabic: 'الناس', verses: 6 },
  ];

  const filteredSurahs = allSurahs.filter(surah => 
    surah.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    surah.arabic.includes(searchQuery) ||
    surah.id.toString().includes(searchQuery)
  );

  const getAudioUrl = (surahNumber) => {
    const paddedNumber = String(surahNumber).padStart(3, '0');
    // Using a more reliable CDN source
    return `https://cdn.islamic.network/quran/audio-surah/128/ar.alafasy/${surahNumber}.mp3`;
  };

  // Fetch verse data with footnotes and transliteration
  const fetchVerseData = async (surahId) => {
    setLoadingVerses(true);
    try {
      const [arabicRes, transRes, translitRes] = await Promise.all([
        axios.get('https://api.quran.com/api/v4/quran/verses/uthmani', {
          params: { chapter_number: surahId }
        }),
        // Using Translation ID 89 (Sherif Ahmeti) - Albanian translation
        axios.get('https://api.quran.com/api/v4/quran/translations/89', {
          params: { chapter_number: surahId }
        }),
        // Better transliteration from alquran.cloud
        axios.get(`https://api.alquran.cloud/v1/surah/${surahId}/en.transliteration`)
      ]);

      const arabicVerses = arabicRes.data.verses || [];
      const transVerses = transRes.data.translations || [];
      const translitAyahs = translitRes.data?.data?.ayahs || [];

      if (arabicVerses.length === 0) {
        toast.error('Nuk mund të ngarkohet teksti arab');
        setVerseData(null);
        setLoadingVerses(false);
        return;
      }

      const verses = arabicVerses.map((arabic, index) => {
        const trans = transVerses[index] || {};
        const translit = translitAyahs[index] || {};
        return {
          verse_number: arabic.verse_number,
          verse_key: arabic.verse_key,
          text_arabic: arabic.text_uthmani,
          text_transliteration: translit.text || '',
          text_albanian: trans.text || 'Përkthimi nuk është i disponueshëm',
          foot_note: trans.foot_note || null,
          resource_name: trans.resource_name || 'Sherif Ahmeti'
        };
      });

      // Add Bismillah for all surahs except Al-Fatiha (1) and At-Tawbah (9)
      const shouldShowBismillah = surahId !== 1 && surahId !== 9;

      setVerseData({
        surah_number: surahId,
        total_verses: verses.length,
        verses,
        show_bismillah: shouldShowBismillah
      });
      
      toast.success(`${verses.length} ajete u ngarkuan me sukses`);
    } catch (error) {
      console.error('Error fetching verses:', error);
      toast.error('Gabim në ngarkimin e përkthimit. Provoni përsëri.');
      setVerseData(null);
    } finally {
      setLoadingVerses(false);
    }
  };

  // Parse footnote text to render superscripts
  const parseFootnotes = (text) => {
    if (!text) return text;
    
    // Replace <sup foot_note=XXXXX>N</sup> with clickable footnote
    const regex = /<sup foot_note=(\d+)>(\d+)<\/sup>/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(<span key={`text-${keyIndex++}`}>{text.substring(lastIndex, match.index)}</span>);
      }
      
      // Add footnote link
      const footnoteId = match[1];
      const footnoteNum = match[2];
      parts.push(
        <sup 
          key={`footnote-${footnoteId}-${keyIndex++}`}
          className="text-accent cursor-pointer hover:underline font-semibold"
          onClick={() => setShowFootnotes(prev => ({...prev, [footnoteId]: !prev[footnoteId]}))}
        >
          [{footnoteNum}]
        </sup>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(<span key={`text-end-${keyIndex}`}>{text.substring(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : text;
  };


  const handlePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setLoading(true);
        
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              setLoading(false);
            })
            .catch((error) => {
              console.error('Error playing audio:', error);
              setLoading(false);
              
              // Try fallback audio source
              if (!audioRef.current.src.includes('mp3quran.net')) {
                toast.info('Duke provuar burim alternativ...');
                const paddedNumber = String(selectedSurah.id).padStart(3, '0');
                audioRef.current.src = `https://server8.mp3quran.net/afs/${paddedNumber}.mp3`;
                audioRef.current.play()
                  .then(() => {
                    setIsPlaying(true);
                    setLoading(false);
                  })
                  .catch(() => {
                    toast.error('Audio nuk mund të ngarkohet.');
                  });
              } else {
                toast.error('Audio nuk mund të luhet.');
              }
            });
        }
      }
    }
  };

  const handleSurahSelect = async (surah) => {
    setSelectedSurah(surah);
    setIsPlaying(false);
    setProgress(0);
    setLoading(false);
    setCurrentVerseIndex(0);
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
    }
    
    await fetchVerseData(surah.id);
  };

  useEffect(() => {
    if (audioRef.current && selectedSurah) {
      const audio = audioRef.current;
      
      const updateProgress = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentVerseIndex(0);
      };

      const handleError = () => {
        toast.error('Gabim në ngarkimin e audios');
        setIsPlaying(false);
        setLoading(false);
      };

      audio.addEventListener('timeupdate', updateProgress);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      return () => {
        audio.removeEventListener('timeupdate', updateProgress);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    }
  }, [selectedSurah]);

  return (
    <section id="kurani" className="py-16 md:py-24 bg-muted" data-testid="quran-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4">Kur'ani Fisnik</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Dëgjoni dhe lexoni Kur'anin me përkthim dhe transliterim
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Surah List - SAME AS BEFORE */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-card rounded-2xl shadow-md p-6 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-accent" />
                <h3 className="font-heading font-semibold text-xl">Surret</h3>
              </div>
              
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Kërko surre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <p className="text-xs text-muted-foreground mb-3">
                {filteredSurahs.length} surre
              </p>

              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-4">
                  {filteredSurahs.map((surah) => (
                    <button
                      key={surah.id}
                      onClick={() => handleSurahSelect(surah)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                        selectedSurah?.id === surah.id
                          ? 'bg-primary text-white shadow-lg'
                          : 'bg-muted hover:bg-primary/10'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs opacity-60">#{surah.id}</span>
                            <p className="font-semibold">{surah.name}</p>
                          </div>
                          <p className="text-sm opacity-80">{surah.verses} ajete</p>
                        </div>
                        <p className="font-quran text-2xl">{surah.arabic}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </motion.div>

          {/* Reader & Translation */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            {selectedSurah ? (
              <div className="space-y-6">
                {/* Audio Player Card */}
                <div className="bg-card rounded-2xl shadow-md p-6">
                  <div className="text-center mb-6 pb-6 border-b border-gray-200">
                    <h3 className="font-quran text-5xl text-primary mb-3 leading-relaxed">
                      {selectedSurah.arabic}
                    </h3>
                    <p className="font-heading text-3xl font-bold text-foreground mb-2">
                      {selectedSurah.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Surja #{selectedSurah.id} • {selectedSurah.verses} Ajete
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-primary" />
                        <div>
                          <span className="font-medium block">Mishary Alafasy</span>
                          <span className="text-xs text-muted-foreground">
                            {isPlaying 
                              ? `Po lexon ajeti ${currentVerseIndex + 1} nga ${verseData?.total_verses || 0}`
                              : 'Recitim i plotë'
                            }
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={handlePlay}
                        disabled={loading}
                        className="p-3 rounded-full bg-primary hover:bg-primary-700 text-white transition-colors shadow-lg disabled:opacity-50"
                      >
                        {loading ? (
                          <Loader className="w-6 h-6 animate-spin" />
                        ) : isPlaying ? (
                          <PauseCircle className="w-6 h-6" />
                        ) : (
                          <PlayCircle className="w-6 h-6" />
                        )}
                      </button>
                    </div>
                    
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    <audio
                      ref={audioRef}
                      src={selectedSurah ? getAudioUrl(selectedSurah.id) : ''}
                      preload="metadata"
                    />
                  </div>
                </div>

                {/* Verses with Translation & Highlighting */}
                <div className="bg-card rounded-2xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-accent" />
                      <h3 className="font-heading font-semibold text-xl">Teksti & Përkthimi</h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowTranslation(!showTranslation)}
                        className="text-sm px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        {showTranslation ? 'Fshih' : 'Shfaq'} Përkthimin
                      </button>
                    </div>
                  </div>

                  {loadingVerses ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="w-8 h-8 animate-spin text-primary" />
                      <span className="ml-3 text-muted-foreground">Duke ngarkuar përkthimin...</span>
                    </div>
                  ) : verseData ? (
                    <div>
                      {/* Translator info */}
                      <div className="mb-4 pb-4 border-b border-gray-100">
                        <p className="text-xs text-muted-foreground text-center">
                          Përkthyes: <span className="font-semibold text-primary">Sherif Ahmeti</span>
                        </p>
                      </div>
                      
                      <ScrollArea className="h-[600px]">
                      <div className="space-y-6 pr-4">
                        {/* Bismillah Header */}
                        {verseData.show_bismillah && (
                          <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="text-center py-8 border-b-2 border-primary/20 mb-8"
                          >
                            <p className="font-quran text-4xl md:text-5xl text-primary leading-loose mb-3">
                              بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ
                            </p>
                            <p className="text-sm text-muted-foreground italic">
                              Me emrin e All-llahut, Mëshiruesit, Mëshirëbërësit!
                            </p>
                          </motion.div>
                        )}
                        
                        {verseData.verses.map((verse, index) => (
                          <motion.div
                            key={verse.verse_number}
                            ref={el => verseRefs.current[index] = el}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.02 }}
                            className="pb-6 border-b border-gray-100 last:border-0 -mx-4 px-4 py-4 rounded-lg"
                          >
                            {/* Verse Number */}
                            <div className="flex items-start mb-3">
                              <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-primary/10 text-primary">
                                {verse.verse_number}
                              </span>
                            </div>

                            {/* Arabic Text */}
                            <div className="text-right mb-4">
                              <p className="font-quran text-2xl md:text-3xl leading-loose text-foreground">
                                {verse.text_arabic}
                              </p>
                            </div>

                            {/* Transliteration */}
                            {verse.text_transliteration && (
                              <div className="mb-3">
                                <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
                                  {verse.text_transliteration}
                                </p>
                              </div>
                            )}

                            {/* Albanian Translation with Footnotes */}
                            <AnimatePresence>
                              {showTranslation && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    {parseFootnotes(verse.text_albanian)}
                                  </p>
                                  
                                  {/* Show footnote if present and clicked */}
                                  {verse.foot_note && Object.keys(showFootnotes).some(key => verse.text_albanian?.includes(key) && showFootnotes[key]) && (
                                    <div className="mt-3 p-3 bg-primary/5 rounded border-l-4 border-accent">
                                      <div className="flex items-start gap-2">
                                        <Info className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                                        <p className="text-sm text-muted-foreground italic">{verse.foot_note}</p>
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    </ScrollArea>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <p>Zgjidhni një surre për të filluar</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-2xl shadow-md p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-3">Zgjidhni një surre</h3>
                  <p className="text-muted-foreground mb-4">
                    Klikoni në një surre për të filluar dëgjimin dhe leximin
                  </p>
                  <div className="bg-primary/5 rounded-lg p-4 text-sm text-left">
                    <p className="font-semibold text-primary mb-2">✨ Veçoritë:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Transliterim (lexim fonetik)</li>
                      <li>• Përkthim shqip me footnotes</li>
                      <li>• Audio nga Mishary Alafasy</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuranReaderWithSync;
